<template>
  <v-dialog
    v-if="!!i"
    :value="!!i"
    max-width="700px"
    persistent
    @keydown.esc="done(false)"
  >
    <v-card data-test="instruction-edit-card" :loading="loading">
      <input
        type="file"
        accept="video/*, .pdf"
        ref="file"
        style="display: none"
        v-on:change="uploadOrChange"
      />
      <v-card-title>
        <span class="headline">{{ $t("instructions.instruction") }}</span>
        <v-spacer />
        <v-btn icon @click="done(false)"><v-icon>close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>
        <v-container grid-list-md>
          <v-form ref="form">
            <v-row dense>
              <v-col cols="12">
                <v-combobox
                  v-model="i.code"
                  :items="instructions"
                  :label="$t('instructions.column.code')"
                  item-value="code"
                  item-text="code"
                  @change="instructionSelected"
                  :rules="[rules.required, rules.validFileName, rules.length64]"
                  required
                  data-test="instruction-edit-code"
                />
              </v-col>

              <v-col cols="12">
                <div class="text-subtitle-1">
                  {{ $t("instructions.column.tags") }}
                </div>
                <template v-for="(tagsGroup, groupIndex) in i.tagsGroups">
                  <v-autocomplete
                    :key="groupIndex"
                    v-model="i.tagsGroups[groupIndex]"
                    :items="tags"
                    :label="`${groupIndex + 1}.`"
                    required
                    chips
                    :item-text="getDetailedTagText"
                    item-value="id"
                    multiple
                    deletable-chips
                    :rules="[rules.tagGroupsLength]"
                    :placeholder="$t('instructions.placeholder.tags')"
                    data-test="instruction-edit-tags"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        v-bind="data.attrs"
                        :input-value="data.selected"
                        close
                        @click="data.select"
                        @click:close="removeTag(groupIndex, data.item)"
                      >
                        {{ getDetailedTagText(data.item) }}
                      </v-chip>
                    </template>
                    <template v-slot:append-outer>
                      <v-icon
                        v-if="i.tagsGroups.length > 1"
                        :key="`removeButton${groupIndex}`"
                        @click="() => i.tagsGroups.splice(groupIndex, 1)"
                        >delete_forever</v-icon
                      >
                    </template>
                  </v-autocomplete>
                  <div
                    v-if="groupIndex + 1 < i.tagsGroups.length"
                    class="text-subtitle-1"
                    :key="`orLabel${groupIndex}`"
                  >
                    {{ $t("instructions.edit.or") }}
                  </div>
                </template>
                <v-btn
                  text
                  @click="() => i.tagsGroups.push([])"
                  color="primary"
                  :loading="uploading"
                >
                  <v-icon left>mdi-plus</v-icon>
                  {{ $t("instructions.edit.addTagGroup") }}
                </v-btn>
              </v-col>
              <v-col cols="12">
                <employees-table-dialog-with-query
                  :variables="{ tagIdsGroups: i.tagsGroups }"
                  :text="$t('instructions.edit.employeesByTags')"
                  :title="i.name"
                />
                <employees-table-dialog-with-query
                  v-if="i && i.id"
                  :variables="{ instructionId: i.id }"
                  :text="$t('instructions.edit.employeesManuallyAssigned')"
                  :title="i.name"
                />
              </v-col>

              <v-col cols="12">
                <v-autocomplete
                  v-model="i.groups"
                  :items="groups"
                  :label="$t('instructions.column.groups')"
                  required
                  chips
                  item-text="name"
                  item-value="id"
                  hide-details="auto"
                  deletable-chips
                  :rules="[rules.required]"
                  multiple
                  @input="handleGroupsInput"
                >
                </v-autocomplete>
              </v-col>

              <v-col cols="12">
                <v-autocomplete
                  v-model="i.modes"
                  :items="modes"
                  :label="$t('instructions.column.modes')"
                  required
                  chips
                  item-text="name"
                  item-value="id"
                  multiple
                  deletable-chips
                  hide-details
                  :placeholder="$t('instructions.placeholder.modes')"
                  data-test="instruction-edit-modes"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      close
                      @click="data.select"
                      @click:close="removeMode(data.item)"
                    >
                      {{ data.item.name }}
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="i.version.revision"
                  :label="$t('instructions.column.revision')"
                  :rules="[rules.required, rules.validFileName, rules.length64]"
                  required
                  @change="revisionChange($event)"
                  data-test="instruction-edit-revision"
                  :hint="
                    previousRevision ? $t('validation.revisionWarning') : ''
                  "
                  class="red-background-hint multiline"
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <v-menu
                  v-model="datePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="$options.filters.date(i.version.date)"
                      :label="
                        $t(
                          'management.mentorship.feedback.column.revisionCreated'
                        )
                      "
                      prepend-inner-icon="event"
                      @click:prepend-inner="datePicker = true"
                      v-on="on"
                      readonly
                      data-test="instruction-edit-date"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="i.version.date"
                    first-day-of-week="1"
                    @input="datePicker = false"
                    no-title
                    scrollable
                    :rules="[rules.required]"
                    required
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="3">
                <v-text-field
                  v-model="i.version.dueInDays"
                  :label="$t('instructions.column.dueInDays')"
                  prepend-inner-icon="event"
                  :rules="[rules.dueInDaysNumber, rules.required]"
                  type="number"
                  data-test="instruction-edit-dueInDays"
                  :disabled="!i.isSignable"
                ></v-text-field>
              </v-col>

              <v-col cols="6">
                <v-checkbox
                  v-model="haveRereadingInDays"
                  :label="$t('instructions.column.haveReviewInDays')"
                  data-test="instruction-edit-rereading"
                  :disabled="!i.isSignable"
                ></v-checkbox>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="i.version.rereadingInDays"
                  :label="$t('instructions.column.reviewInDays')"
                  prepend-inner-icon="refresh"
                  :disabled="!haveRereadingInDays || !i.isSignable"
                  :rules="[rules.rereadingInDaysNumber]"
                  type="number"
                  data-test="instruction-edit-rereadingDays"
                ></v-text-field>
              </v-col>

              <v-col dense>
                <v-checkbox
                  v-model="notSignable"
                  :label="$t('instructions.edit.notSignable')"
                ></v-checkbox>
              </v-col>

              <v-col
                cols="12"
                class="mt-2"
                v-if="showFilesRequired && !i.version.files.length"
              >
                <v-alert color="red" text type="error" class="my-0">
                  {{ $t("validation.required") }}
                </v-alert>
              </v-col>
              <v-col cols="12">
                <v-row
                  v-for="(f, index) in i.version.files"
                  :key="`file-${f.file.id}`"
                  class="my-0 py-0"
                >
                  <v-col cols="12">
                    <div :key="`local${i}`" class="font-weight-bold">
                      {{ $t("instructions.edit.language") }} {{ index + 1 }}
                    </div>
                  </v-col>
                  <v-col class="my-0 py-0">
                    <a
                      target="_blank"
                      :href="f.file.url"
                      style="display: inline-block; margin-top: 10px"
                    >
                      {{ f.file.name }}
                    </a>
                    <v-btn
                      v-if="!f.id"
                      icon
                      class="ml-4"
                      color="primary"
                      style="margin-top: -4px"
                      @click="removeFile(f.file.id)"
                    >
                      <v-icon>close</v-icon>
                    </v-btn>
                  </v-col>
                  <v-col class="text-end">
                    <v-btn
                      :disabled="!f.language"
                      v-if="f.id"
                      text
                      color="primary"
                      :loading="uploading"
                      @click="
                        () => {
                          localeFileId = f.id;
                          $refs.file.click();
                        }
                      "
                      class="text-end"
                      ><v-icon left>note_add</v-icon
                      >{{ $t("instructions.edit.changeFile") }}
                    </v-btn></v-col
                  >

                  <v-col cols="12" class="py-1">
                    <v-text-field
                      v-model="f.name"
                      :label="$t('instructions.edit.name')"
                      small
                      :hint="getFileNameHint(f)"
                      persistent-hint
                      :rules="[rules.required]"
                    />
                  </v-col>

                  <v-col cols="12" class="py-1">
                    <v-select
                      v-model="f.language"
                      @change="handleFileLanguageChange(f)"
                      :items="locales"
                      item-text="text"
                      item-value="value"
                      hide-details="auto"
                      :label="$t('instructions.edit.language')"
                      small
                      :rules="[rules.localeExists]"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" v-if="availableLocales.length">
                <v-btn
                  text
                  @click="validateAndAddOrChangeFile"
                  color="primary"
                  :loading="uploading"
                >
                  <v-icon left>mdi-plus</v-icon>
                  {{ $t("instructions.edit.addFile") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey darken-1"
          text
          @click="done(false)"
          data-test="instruction-edit-cancel"
        >
          {{ $t("dialog.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          text
          :loading="loading"
          @click="save"
          data-test="instruction-edit-save"
        >
          {{ $t("dialog.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <revoke-modal
      v-if="commentDialog.show"
      :show="commentDialog.show"
      :title="$t(`fileChange.title`)"
      :text="$t(`fileChange.comment`)"
      :action="$t(`dialog.change`)"
      @confirm="commentDialog.callback"
      @cancel="commentDialog.show = false"
    />
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

import EditInstructionsQuery from "@/graphql/queries/EditInstructions.gql";
import ChangeInstructionQuery from "@/graphql/mutation/instruction/ChangeInstruction.gql";
import TagsQuery from "@/graphql/Tags.gql";
import ModesQuery from "@/graphql/queries/mode/Modes.gql";
import UploadFileQuery from "@/graphql/mutation/UploadFile.gql";
import InstructionGroupsQuery from "@/graphql/queries/InstructionGroups.gql";
import ChangeFileQuery from "@/graphql/mutation/ChangeFile.gql";

import EmployeesTableDialogWithQuery from "@/components/employee/EmployeesTableDialogWithQuery";

import RevokeModal from "@/components/RevokeModal";

import { getDetailedTagText } from "@/utils/tag";

export default {
  props: {
    instructionId: Number,
  },
  components: {
    EmployeesTableDialogWithQuery,
    RevokeModal,
  },
  data() {
    return {
      uploading: false,
      loading: false,
      instructions: [],
      i: null,
      datePicker: false,
      showFilesRequired: false,
      localeFileId: null,
      previousVersionLocalsNamesByLanguage: new Map(),
      previousRevision: null,
      commentDialog: {
        show: false,
        callback: () => {},
      },
      rules: {
        length64: (value) => {
          return (
            /^.{1,64}$/.test(value) || this.$i18n.tc("validation.length", 64)
          );
        },
        tagGroupsLength: (value) => {
          if (this.i.tagsGroups.length == 1) {
            return true;
          } else {
            return value.length > 0 || this.$i18n.t("validation.required");
          }
        },
        dueInDaysNumber: (value) => {
          return Number(value) > 0 || this.$i18n.t("validation.positiveNumber");
        },
        rereadingInDaysNumber: (value) => {
          if (!this.haveRereadingInDays) {
            return true;
          } else {
            return (
              Number(value) > 0 || this.$i18n.t("validation.positiveNumber")
            );
          }
        },
        validFileName: (value) => {
          return (
            /^[0-9a-zA-ZäÄöÖüÜõÕ_\-. ]+$/.test(value) ||
            this.$i18n.t("validation.allowedFileNameSymbols")
          );
        },
        localeExists: (value) => {
          const localeCount =
            this.i &&
            this.i.version.files.length &&
            this.i.version.files
              .map((l) => l.language)
              .reduce((count, locale) => {
                if (locale == value) {
                  count++;
                }
                return count;
              }, 0);
          return localeCount <= 1 || this.$i18n.t("validation.localeExists");
        },
        required: (value) =>
          (Array.isArray(value) && value.length > 0) ||
          (!!value && !Array.isArray(value)) ||
          this.$i18n.t("validation.required"),
      },
    };
  },
  computed: {
    ...mapGetters(["locales"]),
    availableLocales() {
      const v = this.i.version;
      return this.locales.filter(
        (l) => !v.files.some((f) => f.language == l.value)
      );
    },
    haveRereadingInDays: {
      get() {
        return this.i.version.rereadingInDays !== null;
      },
      set(value) {
        const v = this.i.version;
        if (value && v.rereadingInDays === null) {
          v.rereadingInDays = 60;
        }
        if (!value) {
          v.rereadingInDays = null;
        }
      },
    },
    notSignable: {
      get() {
        return !this.i.isSignable;
      },
      set(val) {
        this.i.isSignable = !val;
      },
    },
  },
  methods: {
    handleFileLanguageChange(file) {
      if (file.language) {
        const previousName = this.previousVersionLocalsNamesByLanguage.get(
          file.language
        );

        if (previousName) {
          file.name = previousName;
          return;
        }
        file.name = "";
      }
    },
    getFileNameHint(file) {
      if (file.language) {
        const previousName = this.previousVersionLocalsNamesByLanguage.get(
          file.language
        );

        if (
          previousName &&
          previousName == file.name &&
          this.previousRevision != this.i.version.revision
        ) {
          return `${this.$t("instructions.edit.nameRestoredFromVersion")} ${
            this.previousRevision
          }`;
        }

        return "";
      }
    },
    getDetailedTagText(item) {
      return getDetailedTagText(item);
    },
    validateAndAddOrChangeFile() {
      if (!this.$refs.form.validate()) {
        return;
      }
      this.$refs.file.click();
    },

    revisionChange(val) {
      this.i.version = this.getEmptyVersion(val);
    },
    reloadInstructions() {
      return this.$apollo
        .query({
          query: EditInstructionsQuery,
        })
        .then((res) => {
          this.instructions = res.data.instructions.map((i) => ({
            ...i,
            tagsGroups: i.tagsGroups.map((g) => g.map((t) => t.id)),
            modes: i.modes.map((m) => m.id),
            groups: i.groups.map((g) => g.id),
          }));
        });
    },

    instructionSelected(sel) {
      // try to find existing instruction by code
      if (typeof sel === "string") {
        sel = this.instructions.find((_i) => _i.code === sel) || sel;
      }

      // existing instruction selected
      if (typeof sel === "object" && sel !== null) {
        Object.assign(this.i, {
          id: sel.id,
          code: sel.code,
          name: sel.name,
          tagsGroups: sel.tagsGroups,
          modes: sel.modes,
          groups: sel.groups,
          isSignable: sel.isSignable,
        });
      }

      // non-existing instruction code entered
      // while editing existing instruction
      else if (this.i.id) {
        Object.assign(this.i, {
          // code: leave as-is
          id: null,
          name: "",
          tagsGroups: [[]],
          modes: [],
          groups: [],
          isSignable: true,
        });
      }
    },
    uploadOrChange(e) {
      const local = this.i.version.files.find((f) => f.id == this.localeFileId);
      if (local && local.id) {
        this.changeFile(e, local);
      } else {
        this.uploadFile(e);
      }
    },
    changeFile(e, localeFile, comment) {
      if (!comment) {
        this.commentDialog = {
          callback: (c) => this.changeFile(e, localeFile, c.reason),
          show: true,
        };
      } else {
        this.commentDialog = {
          callback: () => {},
          show: false,
        };
        const files = e.target.files || e.dataTransfer.files;
        const file = files.length && files[0];

        this.$apollo
          .mutate({
            mutation: ChangeFileQuery,
            variables: {
              oldFileId: localeFile.file.id,
              code: this.i.code,
              revision: this.i.version.revision,
              language: localeFile.language,
              upload: file,
              comment: comment,
            },
          })
          .then((res) => {
            localeFile.file = res.data.file;
          })
          .catch((err) =>
            this.$store.commit("push_global_message", {
              color: "error",
              text: `${err}`,
            })
          )
          .finally(() => {
            this.localeFileId = null;
            this.uploading = false;
          });
      }
    },

    uploadFile(e) {
      const files = e.target.files || e.dataTransfer.files;
      const file = files.length && files[0];
      if (!file) return;
      this.uploading = true;
      e.target.value = null;
      this.$apollo
        .mutate({
          mutation: UploadFileQuery,
          variables: { file },
        })
        .then((res) => {
          const nextFileLanguage = this.nextFileLanguage();
          this.i.version.files.push({
            language: nextFileLanguage,
            name: this.getPreviousLocalNameOrEmpty(nextFileLanguage),
            file: res.data.file,
          });
        })
        .finally(() => {
          this.uploading = false;
        });
    },

    nextFileLanguage() {
      return (
        this.locales
          .map((l) => l.value)
          .find(
            (lang) => !this.i.version.files.find((f) => f.language === lang)
          ) || null
      );
    },

    getPreviousLocalNameOrEmpty(nextFileLanguage) {
      const previousName = this.previousVersionLocalsNamesByLanguage.get(
        nextFileLanguage
      );

      return previousName || "";
    },

    save() {
      if (!this.$refs.form.validate()) {
        this.$store.commit("push_global_message", {
          color: "error",
          text: this.$i18n.t("error.saveFailed"),
        });
        const firstInvalidField = this.$refs.form.inputs.find(
          (input) => !input.valid
        );
        if (firstInvalidField) {
          firstInvalidField.$el.scrollIntoView({
            behavior: "smooth",
          });
        }
        return;
      }

      if (!this.i.version.files.length) {
        this.showFilesRequired = true;
        return;
      }

      const i = this.i;
      const v = this.i.version;
      const instruction = {
        id: i.id || undefined,
        code: i.code.trim(),
        tagIDsGroups: i.tagsGroups,
        modes: i.modes,
        groups: i.groups,
        isSignable: i.isSignable,
        version: {
          id: v.id || undefined,
          revision: v.revision,
          date: new Date(v.date).toISOString(),
          dueInDays: v.dueInDays,
          rereadingInDays: v.rereadingInDays,
          files: v.files.map((f) => ({
            id: f.id || null,
            language: f.language,
            fileId: f.file.id,
            name: f.name,
          })),
        },
      };
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: ChangeInstructionQuery,
          variables: {
            instruction,
          },
        })
        .then(this.done)
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: `${err}`,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    done(updated = true) {
      this.i = null;
      if (updated) {
        this.$emit("updated");
      }
    },

    removeTag(group, item) {
      const tags = this.i.tagsGroups[group];
      if (!tags) return;
      const index = tags.indexOf(item.id);
      if (index >= 0) tags.splice(index, 1);
    },
    removeMode(item) {
      const index = this.i.modes.indexOf(item.id);
      if (index >= 0) this.i.modes.splice(index, 1);
    },
    removeFile(id) {
      const index = this.i.version.files.findIndex((f) => f.file.id === id);
      if (index >= 0) this.i.version.files.splice(index, 1);
    },
    handleGroupsInput(items) {
      if (!items.length) {
        this.i.groups = [];
      } else {
        this.i.groups = [this.i.groups[this.i.groups.length - 1]];
      }
    },
    getEmptyVersion(revision) {
      const now = new Date();

      return {
        id: null,
        revision: revision,
        date: now.toISOString(),
        dueInDays: 30,
        files: [],
        rereadingInDays: null,
      };
    },
  },

  watch: {
    instructionId(id) {
      if (id === null) {
        return;
      }

      // allow subsequent instruction editing
      this.$emit("update:instructionId", null);

      // reset error
      this.showFilesRequired = false;

      // make sure the list is up to date before any editing
      const loadInstructions = this.reloadInstructions();

      if (id === 0) {
        // no need to await the list here before showing an empty form

        this.i = {
          id: null,
          code: "",
          name: "",
          tagsGroups: [[]],
          modes: [],
          groups: [],
          isSignable: true,
          version: this.getEmptyVersion(""),
        };
        this.previousRevision = null;
      } else if (id > 0) {
        loadInstructions.then(() => {
          this.i = this.instructions.find((i) => +i.id === id);
          if (!this.i) {
            // force list refresh
            this.done(true);
            return;
          }
          this.previousRevision = this.i.version.revision;
          this.previousVersionLocalsNamesByLanguage = new Map();
          this.i.version.files.forEach((f) => {
            this.previousVersionLocalsNamesByLanguage.set(f.language, f.name);
          });
        });
      }
    },
  },

  apollo: {
    tags: {
      query: TagsQuery,
      fetchPolicy: "cache-and-network",
    },
    modes: {
      query: ModesQuery,
      fetchPolicy: "cache-and-network",
    },
    groups: {
      query: InstructionGroupsQuery,
      fetchPolicy: "cache-and-network",
      skip() {
        return !this.i;
      },
    },
  },
};
</script>
<style>
.red-background-hint .v-messages__message {
  color: white !important;
  background-color: #fb8c00 !important;
  padding: 10px 13px;
  border-radius: 5px;
  max-width: fit-content;
}
/* Because we don't want to target error--text within v-messages__message, error--text will be overridden to default values */
.red-background-hint .error--text .v-messages__message {
  color: #ff5252 !important;
  background-color: transparent !important;
  padding: 0;
  border-radius: 0;
  max-width: none;
}
.multiline {
  white-space: pre-line;
}
</style>
